import { useEffect, useState, useCallback } from 'react'
import { googleDriveAPI } from '../services/integrations/googleDrive'

/**
 * Polls /integrations/google-drive/status/ and returns the workspace's cloud
 * storage state plus convenience derived flags used by the stranded-files
 * banner and the documents pages.
 *
 * Returns:
 *   {
 *     loading,
 *     status,             // raw status payload
 *     isStranded,         // true if there are provider files but no active connection
 *     strandedCount,
 *     providerLabel,
 *     reload,
 *   }
 *
 * Failures are swallowed — the feature is non-essential and should never
 * block document pages from rendering.
 */
const PROVIDER_LABELS = {
  google_drive: 'Google Drive',
  onedrive: 'Microsoft OneDrive',
  dropbox: 'Dropbox',
}

export function useCloudStorageStatus({ pollIntervalMs = 0 } = {}) {
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)

  const reload = useCallback(async () => {
    try {
      const res = await googleDriveAPI.getStatus()
      if (res?.success) {
        const payload = res.data?.data ?? res.data ?? null
        setStatus(payload)
      } else {
        setStatus(null)
      }
    } catch (e) {
      setStatus(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    reload()
  }, [reload])

  useEffect(() => {
    if (!pollIntervalMs || pollIntervalMs < 1000) return undefined
    const t = setInterval(() => { reload() }, pollIntervalMs)
    return () => clearInterval(t)
  }, [pollIntervalMs, reload])

  const isConnected = status?.connected === true
  const providerKey = status?.provider || null
  const providerLabel = providerKey ? (PROVIDER_LABELS[providerKey] || providerKey) : null
  const providerFileCount = status?.provider_file_count || 0
  const strandedCount = (!isConnected && providerFileCount > 0) ? providerFileCount : 0
  const isStranded = strandedCount > 0

  return {
    loading,
    status,
    isConnected,
    providerKey,
    providerLabel,
    strandedCount,
    isStranded,
    reload,
  }
}
